import React, { useEffect, useRef, useState } from 'react';
import { Box, TextField } from '@mui/material';

export interface VerificationCodeProps {
    length: number;
    onChange?: (value: string) => void;
    autoFocus?: boolean;
}

const VerificationCodeInput: React.FC<VerificationCodeProps> = ({
    length,
    onChange,
    autoFocus,
}) => {
    const [values, setValues] = useState(Array(length).fill(''));
    const firstFieldRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (autoFocus && firstFieldRef.current) {
            firstFieldRef.current.focus();
        }
    }, [autoFocus]);

    const handleChange =
        (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
            const newValues = [...values];
            newValues[index] = event.target.value.slice(-1);
            setValues(newValues);

            if (onChange) {
                onChange(newValues.join('')); // Report the joined string to parent
            }

            // Automatically move focus to the next field
            if (event.target.value && index < length - 1) {
                const nextField = document.getElementById(`code-input-${index + 1}`);
                if (nextField) nextField.focus();
            }
        };

    const handleKeyDown =
        (index: number) => (event: React.KeyboardEvent<HTMLInputElement>) => {
            // Handle Backspace to move to the previous field
            if (event.key === 'Backspace' && !values[index] && index > 0) {
                const prevField = document.getElementById(`code-input-${index - 1}`);
                if (prevField) prevField.focus();
            }
        };

    return (
        <Box display='flex' alignItems='center' gap={1} padding={2}>
            {values.map((value, index) => (
                <TextField
                    key={index}
                    id={`code-input-${index}`}
                    value={value}
                    onChange={handleChange(index)}
                    onKeyDown={handleKeyDown(index)}
                    variant='outlined'
                    inputRef={index === 0 ? firstFieldRef : null}
                    inputProps={{
                        maxLength: 1,
                        style: { textAlign: 'center', minWidth: '10px' },
                        inputMode: 'text',
                        autoCapitalize: 'off',
                        autoCorrect: 'off',
                        spellCheck: 'false',
                    }}
                />
            ))}
        </Box>
    );
};

export default VerificationCodeInput;
